import { useCallback, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useOnMountUnsafe } from '@make-software/tg-mini-app-ui';

import { FRIENDS_PATH, HACKATHON_PROJECT_DETAILS_PATH, HOME_PATH } from '@/app/paths';
import { useSeasonInfo, useCollect, useHackathonProjects, fetchVoteRanking, selectVoteRanking, AppDispatch } from '@/shared';
import { SortOption } from '@/entities';
import { HACKATHON_QUERY_CONFIG } from '@/constants';

export const useScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { collect } = useCollect();

  const { data: seasonInfo } = useSeasonInfo();

  const {
    items: projects = [],
    loading,
    error,
    canLoadMore,
    loadMore,
    setSort
  } = useHackathonProjects({ query: HACKATHON_QUERY_CONFIG });
  const voteRanking = useSelector(selectVoteRanking);

  useOnMountUnsafe(async () => {
    try {
      await collect({ url: HOME_PATH });
      dispatch(fetchVoteRanking());
    } catch (err) {
      console.error('Failed to track page visit:', err);
    }
  });

  const filters = useMemo<SortOption[]>(
    () => ['Newest', 'Oldest', 'Most popular', 'Least popular'],
    []
  );

  const handleSort = useCallback(
    (label: SortOption) => {
      setSort?.(label);
    },
    [setSort]
  );

  const navigateTo = useCallback(
    (path: string) => () => {
      navigate(path);
    },
    [navigate]
  );

  const navigateToFantomRun = useCallback(() => {
    window.location.href = '/fantom-run/index.html';
  }, []);

  const navigateToProject = useCallback(
    (documentId: string) => {
      navigate(generatePath(HACKATHON_PROJECT_DETAILS_PATH, { documentId }));
    },
    [navigate]
  );

  const roundVoteRanking = useMemo(
    () =>
      seasonInfo?.airdrop.round === 1 ? (voteRanking?.round1 ?? []) : (voteRanking?.round2 ?? []),
    [voteRanking, seasonInfo]
  );

  const onInviteBannerPress = () => navigate(FRIENDS_PATH);

  return {
    projects,
    loading,
    error,
    canLoadMore,
    loadMore,
    filters,
    handleSort,
    navigateToFantomRun,
    navigateToProject,
    roundVoteRanking,
    onInviteBannerPress,
    navigateTo
  };
};
